import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import DoneAllIcon from "@mui/icons-material/DoneAll"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import RemoveIcon from "@mui/icons-material/Remove"
import {
  Box,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"

import GridTable from "@components/grid-table"
import { GRAY_COLOR, GRID_HEADER_ITEM, PRIMARY } from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import { listDeliveries } from "@domain/changelog"
import {
  ChangelogChannel,
  ChangelogDeliveryRow,
  ChangelogDeliveryStatus
} from "@models/changelog"

import { formatShortDate, reactionEmoji } from "../shared"

const SENT_COLOR = "#1E9E5A"
const FAILED_COLOR = "#D14343"
const SENDING_COLOR = "#D99000"

const STATUS_COLOR: Record<string, string> = {
  [ChangelogDeliveryStatus.SENT]: SENT_COLOR,
  [ChangelogDeliveryStatus.SENDING]: SENDING_COLOR,
  [ChangelogDeliveryStatus.FAILED]: FAILED_COLOR
}

const CHANNEL_ICON = {
  [ChangelogChannel.EMAIL]: MailOutlineIcon,
  [ChangelogChannel.WHATSAPP]: ChatBubbleOutlineIcon,
  [ChangelogChannel.PUSH]: NotificationsNoneIcon
}

// Fixed display order — all three icons always render (gray when this recipient
// had no delivery attempt on that channel).
const CHANNEL_ORDER: ChangelogChannel[] = [
  ChangelogChannel.EMAIL,
  ChangelogChannel.WHATSAPP,
  ChangelogChannel.PUSH
]

const CHANNEL_LABEL_KEY: Record<string, string> = {
  [ChangelogChannel.EMAIL]: "changelog_channel_email",
  [ChangelogChannel.WHATSAPP]: "changelog_channel_whatsapp",
  [ChangelogChannel.PUSH]: "changelog_channel_push"
}

const STATUS_LABEL_KEY: Record<string, string> = {
  [ChangelogDeliveryStatus.SENT]: "changelog_delivery_sent",
  [ChangelogDeliveryStatus.SENDING]: "changelog_delivery_sending",
  [ChangelogDeliveryStatus.FAILED]: "changelog_delivery_failed"
}

function announcementTitle(row: ChangelogDeliveryRow): string {
  const translations = row.announcement?.translations || {}
  return (
    translations.tr?.title ||
    Object.values(translations)[0]?.title ||
    row.announcement?._id ||
    "—"
  )
}

function recipientLabel(row: ChangelogDeliveryRow): string {
  const p = row.cPerson
  if (!p) {
    return "—"
  }
  const name = `${p.firstName || ""} ${p.lastName || ""}`.trim()
  return name || p.email || p.phone || p._id
}

function ChannelsCell({
  row,
  translate
}: {
  row: ChangelogDeliveryRow
  translate: (key: string) => string
}) {
  const channels = row.channels || []
  return (
    <Stack direction="row" gap={0.75} alignItems="center">
      {CHANNEL_ORDER.map((ch) => {
        const Icon = CHANNEL_ICON[ch]
        const delivery = channels.find((c) => c.channel === ch)
        const channelLabel = translate(CHANNEL_LABEL_KEY[ch])
        // No attempt on this channel → default gray.
        if (!delivery) {
          return (
            <Tooltip
              key={ch}
              title={`${channelLabel} — ${translate("changelog_delivery_not_sent")}`}
            >
              <Icon fontSize="small" sx={{ color: GRAY_COLOR }} />
            </Tooltip>
          )
        }
        const color = STATUS_COLOR[delivery.status] ?? GRAY_COLOR
        const statusLabel = STATUS_LABEL_KEY[delivery.status]
          ? translate(STATUS_LABEL_KEY[delivery.status])
          : delivery.status
        return (
          <Tooltip
            key={ch}
            title={`${channelLabel} — ${statusLabel}${
              delivery.error ? `: ${delivery.error}` : ""
            }`}
          >
            <Icon fontSize="small" sx={{ color }} />
          </Tooltip>
        )
      })}
    </Stack>
  )
}

const ALL = "all"

export default function DeliveriesSection() {
  const { translate, announcements } = useChangelogPageContext()

  const [announcementId, setAnnouncementId] = useState<string>(ALL)
  const [channel, setChannel] = useState<string>(ALL)
  const [status, setStatus] = useState<string>(ALL)
  const [read, setRead] = useState<string>(ALL)
  const [feedback, setFeedback] = useState<string>(ALL)
  const [pagination, setPagination] = useState({ page: 0, pageSize: 20 })

  const { data: page, isLoading } = useQuery({
    queryKey: [
      "changelog-deliveries",
      announcementId,
      channel,
      status,
      read,
      feedback,
      pagination
    ],
    queryFn: async () =>
      (
        await listDeliveries({
          announcementId: announcementId === ALL ? undefined : announcementId,
          channel: channel === ALL ? undefined : (channel as ChangelogChannel),
          status:
            status === ALL ? undefined : (status as ChangelogDeliveryStatus),
          read: read === ALL ? undefined : read === "read",
          hasFeedback: feedback === ALL ? undefined : feedback === "with",
          pageNumber: pagination.page + 1,
          pageSize: pagination.pageSize
        })
      ).data
  })

  const rows = page?.docs ?? []
  const totalDocs = page?.totalDocs ?? 0

  const columns: GridColDef[] = [
    {
      field: "announcement",
      flex: 2,
      minWidth: 200,
      headerName: translate("changelog_col_title"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_title")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ fontSize: "0.9rem" }} noWrap>
          {announcementTitle(params.row as ChangelogDeliveryRow)}
        </Typography>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "customer",
      flex: 1.3,
      minWidth: 140,
      headerName: translate("changelog_col_customer"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_customer")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ fontSize: "0.85rem" }} noWrap>
          {(params.row as ChangelogDeliveryRow).customer?.companyName || "—"}
        </Typography>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "recipient",
      flex: 1.5,
      minWidth: 160,
      headerName: translate("changelog_col_recipient"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_recipient")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ fontSize: "0.85rem" }} noWrap>
          {recipientLabel(params.row as ChangelogDeliveryRow)}
        </Typography>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "channels",
      flex: 1,
      minWidth: 110,
      headerName: translate("changelog_col_channels"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_channels")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <ChannelsCell
          row={params.row as ChangelogDeliveryRow}
          translate={translate}
        />
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "read",
      flex: 0.7,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      headerName: translate("changelog_col_read"),
      renderHeader: () => (
        <Box
          sx={{ color: GRID_HEADER_ITEM, width: "100%", textAlign: "center" }}
        >
          {translate("changelog_col_read")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) =>
        (params.row as ChangelogDeliveryRow).readAt ? (
          <DoneAllIcon fontSize="small" sx={{ color: PRIMARY }} />
        ) : (
          <RemoveIcon fontSize="small" sx={{ color: GRAY_COLOR }} />
        ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "feedback",
      flex: 1.4,
      minWidth: 150,
      headerName: translate("changelog_col_reaction"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_reaction")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ChangelogDeliveryRow
        if (!row.reaction) {
          return <Typography sx={{ color: GRAY_COLOR }}>—</Typography>
        }
        return (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <span>{reactionEmoji(row.reaction)}</span>
            {row.comment ? (
              <Typography sx={{ fontSize: "0.8rem", color: GRAY_COLOR }} noWrap>
                {row.comment}
              </Typography>
            ) : null}
          </Stack>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "date",
      flex: 1,
      minWidth: 120,
      headerName: translate("changelog_col_date"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_date")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ fontSize: "0.85rem" }}>
          {formatShortDate((params.row as ChangelogDeliveryRow).createdAt)}
        </Typography>
      ),
      sortable: false,
      disableColumnMenu: true
    }
  ]

  const filterSx = { minWidth: "9rem", background: "white" }

  return (
    <Box>
      <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, mb: 0.5 }}>
        {translate("changelog_deliveries")}
      </Typography>
      <Typography sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 2 }}>
        {translate("changelog_deliveries_desc")}
      </Typography>

      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
        <Select
          size="small"
          value={announcementId}
          onChange={(e) => {
            setAnnouncementId(e.target.value)
            setPagination((prev) => ({ ...prev, page: 0 }))
          }}
          sx={{ ...filterSx, maxWidth: "16rem" }}
        >
          <MenuItem value={ALL}>
            {translate("changelog_all_announcements")}
          </MenuItem>
          {announcements.map((a) => (
            <MenuItem key={a._id} value={a._id}>
              {a.translations?.tr?.title ||
                Object.values(a.translations || {})[0]?.title ||
                a._id}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value)
            setPagination((prev) => ({ ...prev, page: 0 }))
          }}
          sx={filterSx}
        >
          <MenuItem value={ALL}>{translate("changelog_all_channels")}</MenuItem>
          <MenuItem value={ChangelogChannel.EMAIL}>
            {translate("changelog_channel_email")}
          </MenuItem>
          <MenuItem value={ChangelogChannel.WHATSAPP}>
            {translate("changelog_channel_whatsapp")}
          </MenuItem>
          <MenuItem value={ChangelogChannel.PUSH}>
            {translate("changelog_channel_push")}
          </MenuItem>
        </Select>

        <Select
          size="small"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPagination((prev) => ({ ...prev, page: 0 }))
          }}
          sx={filterSx}
        >
          <MenuItem value={ALL}>{translate("changelog_all_statuses")}</MenuItem>
          <MenuItem value={ChangelogDeliveryStatus.SENT}>
            {translate("changelog_delivery_sent")}
          </MenuItem>
          <MenuItem value={ChangelogDeliveryStatus.FAILED}>
            {translate("changelog_delivery_failed")}
          </MenuItem>
          <MenuItem value={ChangelogDeliveryStatus.SENDING}>
            {translate("changelog_delivery_sending")}
          </MenuItem>
        </Select>

        <Select
          size="small"
          value={read}
          onChange={(e) => {
            setRead(e.target.value)
            setPagination((prev) => ({ ...prev, page: 0 }))
          }}
          sx={filterSx}
        >
          <MenuItem value={ALL}>
            {translate("changelog_read_all_filter")}
          </MenuItem>
          <MenuItem value="read">{translate("changelog_read_yes")}</MenuItem>
          <MenuItem value="unread">{translate("changelog_read_no")}</MenuItem>
        </Select>

        <Select
          size="small"
          value={feedback}
          onChange={(e) => {
            setFeedback(e.target.value)
            setPagination((prev) => ({ ...prev, page: 0 }))
          }}
          sx={filterSx}
        >
          <MenuItem value={ALL}>{translate("changelog_feedback_all")}</MenuItem>
          <MenuItem value="with">
            {translate("changelog_with_feedback")}
          </MenuItem>
          <MenuItem value="without">
            {translate("changelog_without_feedback")}
          </MenuItem>
        </Select>
      </Stack>

      <GridTable
        loading={isLoading}
        rows={rows}
        columns={columns}
        rowCount={totalDocs}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row: ChangelogDeliveryRow) => row._id}
        disableColumnSelector={true}
        noRowsTableText="changelog_empty_deliveries"
      />
    </Box>
  )
}
